'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Search, SlidersHorizontal, X, ChevronDown, MapPin, Car, Home, Globe } from 'lucide-react';
import VettingBadge from '@/components/ui/VettingBadge';
import StarRating from '@/components/ui/StarRating';
import AppImage from '@/components/ui/AppImage';

// Backend integration point: replace with API call to GET /api/workers?filters=...
const mockWorkers = [
{
  id: 'worker-001',
  displayName: 'Thandi M.',
  anonymous: false,
  vetted: true,
  suburb: 'Waterkloof Ridge',
  category: 'elder-care',
  tags: ['Alzheimer\'s care', 'Dementia support', 'Companionship'],
  rating: 4.9,
  reviewCount: 23,
  age: 34,
  hasLicense: true,
  hasVehicle: true,
  liveIn: true,
  languages: ['English', 'Zulu', 'Sotho'],
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3a1efe8-1772875264718.png",
  photoAlt: 'Smiling woman with natural hair in professional attire',
  available: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  distanceKm: 15,
  bio: 'Dedicated elder care specialist with 8 years of experience supporting families through Alzheimer\'s and dementia journeys.'
},
{
  id: 'worker-002',
  displayName: 'Caregiver #248',
  anonymous: true,
  vetted: false,
  suburb: 'Lynnwood',
  category: 'child-care',
  tags: ['Au pair', 'Homework help', 'Infant care'],
  rating: 4.6,
  reviewCount: 11,
  age: 26,
  hasLicense: false,
  hasVehicle: false,
  liveIn: false,
  languages: ['English', 'Afrikaans'],
  photo: "https://images.unsplash.com/photo-1673799809016-26226917091b",
  photoAlt: 'Young woman smiling outdoors in casual clothes',
  available: ['Mon', 'Wed', 'Fri', 'Sat'],
  distanceKm: 8,
  bio: ''
},
{
  id: 'worker-003',
  displayName: 'Sipho K.',
  anonymous: false,
  vetted: true,
  suburb: 'Centurion',
  category: 'pet-house',
  tags: ['Pet sitting', 'Dog walking', 'House sitting'],
  rating: 5.0,
  reviewCount: 17,
  age: 29,
  hasLicense: true,
  hasVehicle: true,
  liveIn: true,
  languages: ['English', 'Zulu'],
  photo: "https://images.unsplash.com/photo-1601239156071-15ec4c7beff2",
  photoAlt: 'Young man with short hair smiling in outdoor setting',
  available: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  distanceKm: 20,
  bio: 'Reliable house and pet sitter. I treat every home like my own.'
},
{
  id: 'worker-004',
  displayName: 'Caregiver #391',
  anonymous: true,
  vetted: false,
  suburb: 'Hatfield',
  category: 'elder-care',
  tags: ['Mobility support', 'Medication reminders', 'Companionship'],
  rating: 4.2,
  reviewCount: 5,
  age: 41,
  hasLicense: true,
  hasVehicle: false,
  liveIn: false,
  languages: ['English', 'Sotho', 'Tswana'],
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf0633c1-1763295638320.png",
  photoAlt: 'Middle-aged woman in professional clothing with warm expression',
  available: ['Tue', 'Thu', 'Sat'],
  distanceKm: 5,
  bio: ''
},
{
  id: 'worker-005',
  displayName: 'Lerato N.',
  anonymous: false,
  vetted: true,
  suburb: 'Faerie Glen',
  category: 'child-care',
  tags: ['Au pair', 'Infant care', 'Homework help'],
  rating: 4.8,
  reviewCount: 31,
  age: 28,
  hasLicense: true,
  hasVehicle: true,
  liveIn: true,
  languages: ['English', 'Sotho', 'Zulu'],
  photo: "https://images.unsplash.com/photo-1631253601025-27c774909b9a",
  photoAlt: 'Young woman with braided hair smiling warmly',
  available: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  distanceKm: 12,
  bio: 'Experienced au pair specialising in infant and toddler care. Early childhood education background.'
},
{
  id: 'worker-006',
  displayName: 'Caregiver #107',
  anonymous: true,
  vetted: false,
  suburb: 'Brooklyn',
  category: 'pet-house',
  tags: ['Dog walking', 'Pet sitting'],
  rating: 4.4,
  reviewCount: 8,
  age: 22,
  hasLicense: false,
  hasVehicle: false,
  liveIn: false,
  languages: ['English', 'Afrikaans'],
  photo: "https://images.unsplash.com/photo-1659121057391-57fcb58dd37a",
  photoAlt: 'Young man with casual style in outdoor setting',
  available: ['Sat', 'Sun'],
  distanceKm: 3,
  bio: ''
},
{
  id: 'worker-007',
  displayName: 'Nomvula D.',
  anonymous: false,
  vetted: true,
  suburb: 'Moreleta Park',
  category: 'elder-care',
  tags: ['Alzheimer\'s care', 'Mobility support', 'Medication reminders'],
  rating: 4.7,
  reviewCount: 19,
  age: 38,
  hasLicense: true,
  hasVehicle: true,
  liveIn: false,
  languages: ['English', 'Zulu', 'Xhosa'],
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_10069c3d5-1772087121295.png",
  photoAlt: 'Woman in healthcare attire with professional demeanor',
  available: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  distanceKm: 18,
  bio: 'Registered nurse with 12 years of specialised elder care experience.'
},
{
  id: 'worker-008',
  displayName: 'Ruan V.',
  anonymous: false,
  vetted: true,
  suburb: 'Silver Lakes',
  category: 'child-care',
  tags: ['Au pair', 'Homework help'],
  rating: 4.5,
  reviewCount: 14,
  age: 25,
  hasLicense: true,
  hasVehicle: true,
  liveIn: false,
  languages: ['English', 'Afrikaans'],
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_15c6c6881-1763293525984.png",
  photoAlt: 'Young man with friendly smile in casual setting',
  available: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  distanceKm: 25,
  bio: 'Male au pair with a passion for education and outdoor activities.'
}];


const categories = [
{ value: 'all', label: 'All Categories' },
{ value: 'elder-care', label: 'Elder Care' },
{ value: 'child-care', label: 'Child Care & Au Pairing' },
{ value: 'pet-house', label: 'Pet & House Sitting' }];


const suburbs = [
  'All of Gauteng',
  // Pretoria / Tshwane
  'Arcadia', 'Brooklyn', 'Centurion', 'Faerie Glen', 'Garsfontein',
  'Hatfield', 'Irene', 'Lynnwood', 'Menlyn', 'Meyerspark', 'Montana',
  'Moreleta Park', 'Muckleneuk', 'Queenswood', 'Rietfontein', 'Silver Lakes',
  'Sunnyside', 'Waterkloof', 'Waterkloof Ridge',
  // Johannesburg
  'Bedfordview', 'Braamfontein', 'Bryanston', 'Craighall', 'Edenvale',
  'Fourways', 'Greenside', 'Houghton', 'Hyde Park', 'Illovo',
  'Johannesburg CBD', 'Melville', 'Midrand', 'Morningside', 'Northcliff',
  'Parkhurst', 'Parktown', 'Randburg', 'Rosebank', 'Sandton',
  'Soweto', 'Sunninghill', 'Woodmead',
  // East Rand / Ekurhuleni
  'Alberton', 'Benoni', 'Boksburg', 'Brakpan', 'Germiston',
  'Kempton Park', 'Nigel', 'Springs', 'Tembisa',
  // West Rand
  'Carletonville', 'Krugersdorp', 'Randfontein', 'Roodepoort', 'Soweto',
  // South of Johannesburg
  'Lenasia', 'Meyerton', 'Vereeniging', 'Vanderbijlpark',
];

// ─── Cascading location data ───────────────────────────────────────────────
interface CityData {
  suburbs: string[];
}
interface ProvinceData {
  cities: Record<string, CityData>;
}

const locationData: Record<string, ProvinceData> = {
  Gauteng: {
    cities: {
      'All Cities/Areas': { suburbs: ['All Suburbs'] },
      'Pretoria / Tshwane': {
        suburbs: [
          'All Suburbs',
          'Arcadia', 'Brooklyn', 'Centurion', 'Faerie Glen', 'Garsfontein',
          'Hatfield', 'Irene', 'Lynnwood', 'Menlyn', 'Meyerspark', 'Montana',
          'Moreleta Park', 'Muckleneuk', 'Queenswood', 'Rietfontein', 'Silver Lakes',
          'Sunnyside', 'Waterkloof', 'Waterkloof Ridge',
        ],
      },
      'Johannesburg': {
        suburbs: [
          'All Suburbs',
          'Bedfordview', 'Braamfontein', 'Bryanston', 'Craighall', 'Edenvale',
          'Fourways', 'Greenside', 'Houghton', 'Hyde Park', 'Illovo',
          'Johannesburg CBD', 'Melville', 'Midrand', 'Morningside', 'Northcliff',
          'Parkhurst', 'Parktown', 'Randburg', 'Rosebank', 'Sandton',
          'Soweto', 'Sunninghill', 'Woodmead',
        ],
      },
      'East Rand / Ekurhuleni': {
        suburbs: [
          'All Suburbs',
          'Alberton', 'Benoni', 'Boksburg', 'Brakpan', 'Germiston',
          'Kempton Park', 'Nigel', 'Springs', 'Tembisa',
        ],
      },
      'West Rand': {
        suburbs: [
          'All Suburbs',
          'Carletonville', 'Krugersdorp', 'Randfontein', 'Roodepoort',
        ],
      },
      'South Gauteng': {
        suburbs: [
          'All Suburbs',
          'Lenasia', 'Meyerton', 'Vereeniging', 'Vanderbijlpark',
        ],
      },
    },
  },
  'Western Cape': { cities: {} },
  'KwaZulu-Natal': { cities: {} },
  'Eastern Cape': { cities: {} },
  'Free State': { cities: {} },
  'Limpopo': { cities: {} },
  'Mpumalanga': { cities: {} },
  'North West': { cities: {} },
  'Northern Cape': { cities: {} },
};

const provinces = Object.keys(locationData);
const activeProvinces = ['Gauteng'];
// ──────────────────────────────────────────────────────────────────────────


const languages = ['English', 'Afrikaans', 'Zulu', 'Sotho', 'Tswana', 'Xhosa'];

const sortOptions = [
{ value: 'vetted-first', label: 'Vetted first' },
{ value: 'highest-rated', label: 'Highest rated' },
{ value: 'most-reviews', label: 'Most reviews' },
{ value: 'closest', label: 'Closest to you' }];


export default function FindHelpContent() {
  const [search, setSearch] = useState('');
  const [suburb, setSuburb] = useState('All of Gauteng');
  const [category, setCategory] = useState('all');
  const [vettedOnly, setVettedOnly] = useState(false);
  const [hasLicense, setHasLicense] = useState(false);
  const [liveIn, setLiveIn] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState('vetted-first');
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 6;

  // Cascading location state
  const [selectedProvince, setSelectedProvince] = useState('Gauteng');
  const [selectedCity, setSelectedCity] = useState('All Cities/Areas');
  const [selectedSuburb, setSelectedSuburb] = useState('All Suburbs');

  const cityOptions = selectedProvince ? Object.keys(locationData[selectedProvince]?.cities ?? {}) : [];
  const suburbOptions = selectedCity && selectedCity !== 'All Cities/Areas'
    ? locationData[selectedProvince]?.cities[selectedCity]?.suburbs ?? ['All Suburbs']
    : ['All Suburbs'];

  const handleProvinceChange = (province: string) => {
    if (!activeProvinces.includes(province)) return;
    setSelectedProvince(province);
    setSelectedCity('All Cities/Areas');
    setSelectedSuburb('All Suburbs');
    setSuburb('All of Gauteng');
    setPage(1);
  };

  const handleCityChange = (city: string) => {
    setSelectedCity(city);
    setSelectedSuburb('All Suburbs');
    if (city === 'All Cities/Areas') {
      setSuburb('All of Gauteng');
    } else {
      setSuburb(city);
    }
    setPage(1);
  };

  const handleSuburbChange = (sub: string) => {
    setSelectedSuburb(sub);
    if (sub === 'All Suburbs') {
      setSuburb(selectedCity === 'All Cities/Areas' ? 'All of Gauteng' : selectedCity);
    } else {
      setSuburb(sub);
    }
    setPage(1);
  };

  const filtered = useMemo(() => {
    let results = [...mockWorkers];

    if (search) {
      const q = search.toLowerCase();
      results = results.filter(
        (w) => w.displayName.toLowerCase().includes(q) || w.suburb.toLowerCase().includes(q) || w.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (suburb !== 'All of Gauteng') results = results.filter((w) => w.suburb === suburb);
    if (category !== 'all') results = results.filter((w) => w.category === category);
    if (vettedOnly) results = results.filter((w) => w.vetted);
    if (hasLicense) results = results.filter((w) => w.hasLicense);
    if (liveIn) results = results.filter((w) => w.liveIn);
    if (selectedLanguage) results = results.filter((w) => w.languages.includes(selectedLanguage));
    if (minRating > 0) results = results.filter((w) => w.rating >= minRating);

    if (sortBy === 'vetted-first') results.sort((a, b) => (b.vetted ? 1 : 0) - (a.vetted ? 1 : 0) || b.rating - a.rating);else
    if (sortBy === 'highest-rated') results.sort((a, b) => b.rating - a.rating);else
    if (sortBy === 'most-reviews') results.sort((a, b) => b.reviewCount - a.reviewCount);else
    if (sortBy === 'closest') results.sort((a, b) => a.distanceKm - b.distanceKm);

    return results;
  }, [search, suburb, category, vettedOnly, hasLicense, liveIn, selectedLanguage, minRating, sortBy]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const activeFilterCount = [
  suburb !== 'All of Gauteng',
  category !== 'all',
  vettedOnly,
  hasLicense,
  liveIn,
  !!selectedLanguage,
  minRating > 0].
  filter(Boolean).length;

  const clearFilters = () => {
    setSuburb('All of Gauteng');
    setSelectedProvince('Gauteng');
    setSelectedCity('All Cities/Areas');
    setSelectedSuburb('All Suburbs');
    setCategory('all');
    setVettedOnly(false);
    setHasLicense(false);
    setLiveIn(false);
    setSelectedLanguage('');
    setMinRating(0);
    setPage(1);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-extrabold mb-1" style={{ color: 'var(--navy)' }}>
          Find Candidates in Gauteng
        </h1>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          {filtered.length} candidate{filtered.length !== 1 ? 's' : ''} available · Verified candidates shown first
        </p>
      </div>

      {/* Search bar + sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
          <input
            type="text"
            placeholder="Search by name, suburb, or specialisation..."
            value={search}
            onChange={(e) => {setSearch(e.target.value);setPage(1);}}
            className="input-field pl-9" />
          
        </div>
        <div className="relative">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="input-field pr-8 appearance-none cursor-pointer min-w-[180px]">
            
            {sortOptions.map((opt) =>
            <option key={`sort-${opt.value}`} value={opt.value}>{opt.label}</option>
            )}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
        </div>
        <button
          onClick={() => setFilterOpen(!filterOpen)}
          className="flex items-center gap-2 px-4 py-3 rounded-xl border font-semibold text-sm transition-all duration-150 relative"
          style={{ borderColor: activeFilterCount > 0 ? 'var(--primary)' : 'var(--border)', color: activeFilterCount > 0 ? 'var(--primary)' : 'var(--foreground)', background: 'white' }}>
          
          <SlidersHorizontal size={16} />
          Filters
          {activeFilterCount > 0 &&
          <span className="w-5 h-5 rounded-full text-xs font-bold text-white flex items-center justify-center" style={{ background: 'var(--primary)' }}>
              {activeFilterCount}
            </span>
          }
        </button>
      </div>

      {/* Filter panel */}
      {filterOpen &&
      <div className="card p-5 mb-6 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-sm" style={{ color: 'var(--navy)' }}>Filter candidates</h3>
            <div className="flex items-center gap-3">
              {activeFilterCount > 0 &&
            <button onClick={clearFilters} className="text-xs font-semibold hover:underline" style={{ color: 'var(--primary)' }}>
                  Clear all ({activeFilterCount})
                </button>
            }
              <button onClick={() => setFilterOpen(false)} className="p-1 rounded-lg hover:bg-muted">
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Province → City/Area → Suburb (spans full row on small, 3 cols on large) */}
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Location</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {/* Province */}
                <div className="relative">
                  <select
                    value={selectedProvince}
                    onChange={(e) => handleProvinceChange(e.target.value)}
                    className="input-field pr-8 appearance-none cursor-pointer text-sm w-full"
                  >
                    {provinces.map((prov) => (
                      <option
                        key={`prov-${prov}`}
                        value={prov}
                        disabled={!activeProvinces.includes(prov)}
                        style={{ color: activeProvinces.includes(prov) ? 'inherit' : '#9ca3af' }}
                      >
                        {prov}{!activeProvinces.includes(prov) ? ' (coming soon)' : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                </div>

                {/* City / Area */}
                <div className="relative">
                  <select
                    value={selectedCity}
                    onChange={(e) => handleCityChange(e.target.value)}
                    disabled={cityOptions.length === 0}
                    className="input-field pr-8 appearance-none cursor-pointer text-sm w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cityOptions.map((city) => (
                      <option key={`city-${city}`} value={city}>{city}</option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                </div>

                {/* Suburb */}
                <div className="relative">
                  <select
                    value={selectedSuburb}
                    onChange={(e) => handleSuburbChange(e.target.value)}
                    disabled={suburbOptions.length <= 1}
                    className="input-field pr-8 appearance-none cursor-pointer text-sm w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {suburbOptions.map((s) => (
                      <option key={`sub-${s}`} value={s}>{s}</option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                </div>
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Category</label>
              <div className="relative">
                <select value={category} onChange={(e) => {setCategory(e.target.value);setPage(1);}} className="input-field pr-8 appearance-none cursor-pointer text-sm">
                  {categories.map((c) => <option key={`filter-cat-${c.value}`} value={c.value}>{c.label}</option>)}
                </select>
                <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
              </div>
            </div>

            {/* Language */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Language</label>
              <div className="relative">
                <select value={selectedLanguage} onChange={(e) => {setSelectedLanguage(e.target.value);setPage(1);}} className="input-field pr-8 appearance-none cursor-pointer text-sm">
                  <option value="">Any language</option>
                  {languages.map((l) => <option key={`filter-lang-${l}`} value={l}>{l}</option>)}
                </select>
                <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
              </div>
            </div>

            {/* Min rating */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Minimum rating</label>
              <div className="relative">
                <select value={minRating} onChange={(e) => {setMinRating(Number(e.target.value));setPage(1);}} className="input-field pr-8 appearance-none cursor-pointer text-sm">
                  <option value={0}>Any rating</option>
                  <option value={4}>4+ stars</option>
                  <option value={4.5}>4.5+ stars</option>
                  <option value={5}>5 stars only</option>
                </select>
                <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-3 mt-4">
            {[
          { key: 'vetted', label: 'Vetted only', value: vettedOnly, setter: setVettedOnly },
          { key: 'license', label: "Driver\'s licence", value: hasLicense, setter: setHasLicense },
          { key: 'livein', label: 'Live-in available', value: liveIn, setter: setLiveIn }].
          map((toggle) =>
          <button
            key={`toggle-${toggle.key}`}
            onClick={() => {toggle.setter(!toggle.value);setPage(1);}}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150"
            style={{
              background: toggle.value ? 'var(--orange-pale)' : 'white',
              borderColor: toggle.value ? 'var(--primary)' : 'var(--border)',
              color: toggle.value ? 'var(--primary)' : 'var(--muted-foreground)'
            }}>
            
                {toggle.label}
                {toggle.value && <X size={12} />}
              </button>
          )}
          </div>
        </div>
      }

      {/* Results grid */}
      {paginated.length === 0 ?
      <div className="card p-12 text-center">
          <Search size={40} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
          <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--navy)' }}>No candidates match your filters</h3>
          <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>Try removing some filters or searching a different suburb.</p>
          <button onClick={clearFilters} className="btn-primary">Clear all filters</button>
        </div> :

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {paginated.map((worker) =>
        <WorkerCard key={worker.id} worker={worker} />
        )}
        </div>
      }

      {/* Pagination */}
      {totalPages > 1 &&
      <div className="flex items-center justify-between mt-8">
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length} candidates
          </p>
          <div className="flex items-center gap-2">
            <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40 hover:bg-muted transition-colors">
            
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) =>
          <button
            key={`page-${p}`}
            onClick={() => setPage(p)}
            className="w-9 h-9 rounded-lg text-sm font-semibold transition-colors"
            style={{
              background: p === page ? 'var(--primary)' : 'transparent',
              color: p === page ? 'white' : 'var(--foreground)',
              border: p === page ? 'none' : '1px solid var(--border)'
            }}>
            
                {p}
              </button>
          )}
            <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40 hover:bg-muted transition-colors">
            
              Next
            </button>
          </div>
        </div>
      }
    </div>);

}

function WorkerCard({ worker }: {worker: typeof mockWorkers[0];}) {
  const dayAbbrev = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return (
    <div className="card p-5 hover:card-shadow-md transition-all duration-200 group hover:-translate-y-0.5 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="relative flex-shrink-0">
          <AppImage
            src={worker.photo}
            alt={worker.photoAlt}
            width={56}
            height={56}
            className="w-14 h-14 rounded-2xl object-cover" />
          
          {worker.vetted &&
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--vetted-color)' }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-bold text-sm leading-tight" style={{ color: 'var(--navy)' }}>{worker.displayName}</h3>
              <div className="flex items-center gap-1 mt-0.5">
                <MapPin size={11} style={{ color: 'var(--muted-foreground)' }} />
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{worker.suburb}</span>
              </div>
            </div>
            <VettingBadge vetted={worker.vetted} size="sm" />
          </div>
          <div className="mt-1.5">
            <StarRating rating={worker.rating} count={worker.reviewCount} size={12} />
          </div>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5">
        {worker.tags.slice(0, 3).map((tag) =>
        <span key={`worker-tag-${worker.id}-${tag}`} className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'var(--muted)', color: 'var(--foreground)' }}>
            {tag}
          </span>
        )}
      </div>

      {/* Availability days */}
      <div className="flex gap-1">
        {dayAbbrev.map((day) =>
        <span
          key={`avail-${worker.id}-${day}`}
          className="text-xs w-7 h-7 rounded-full flex items-center justify-center font-semibold"
          style={{
            background: worker.available.includes(day) ? 'var(--orange-pale)' : 'var(--muted)',
            color: worker.available.includes(day) ? 'var(--primary)' : 'var(--muted-foreground)'
          }}>
          
            {day[0]}
          </span>
        )}
      </div>

      {/* Attributes */}
      <div className="flex flex-wrap gap-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
        {worker.hasLicense &&
        <span className="flex items-center gap-1">
            <Car size={12} style={{ color: 'var(--success)' }} />
            Has licence
          </span>
        }
        {worker.liveIn &&
        <span className="flex items-center gap-1">
            <Home size={12} style={{ color: 'var(--success)' }} />
            Live-in
          </span>
        }
        <span className="flex items-center gap-1">
          <Globe size={12} />
          {worker.languages.slice(0, 2).join(', ')}
        </span>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-auto pt-2 border-t border-border">
        <Link
          href="/worker-profile-page"
          className="flex-1 px-3 py-2 rounded-xl text-sm font-semibold text-center border transition-all duration-150 hover:bg-muted"
          style={{ borderColor: 'var(--border)', color: 'var(--navy)' }}>
          
          View Profile
        </Link>
        <Link
          href="/sign-up-login?action=message"
          className="flex-1 btn-primary px-3 py-2 text-sm">
          
          Message
        </Link>
      </div>
    </div>);

}